import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { formatCurrency } from '../../utils/currency';
import {
    Building2, Users, DollarSign, TrendingUp, TrendingDown, Percent, Home,
    ArrowUpRight, Plus, Wrench, Receipt, ChevronRight, Clock, CheckCircle2, AlertCircle,
} from 'lucide-react';
import {
    ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import { StatTile, Card, PageHeader, Badge, EmptyState } from '../ui';

const CHART_INCOME   = '#0f766e'; // primary-700
const CHART_EXPENSES = '#f59e0b'; // warning-500
const CHART_GRID     = '#e2e8f0'; // neutral-200
const CHART_AXIS     = '#94a3b8'; // neutral-400

const compact = v => {
    const n = Number(v || 0);
    if (Math.abs(n) >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
    if (Math.abs(n) >= 1_000)     return (n / 1_000).toFixed(0) + 'k';
    return String(n);
};

const Dashboard = () => {
    const { user } = useAuth();
    const [overview, setOverview] = useState(null);
    const [trend, setTrend] = useState([]);
    const [properties, setProperties] = useState([]);
    const [activity, setActivity] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            // Track failures rather than swallowing them: a request that errors must
            // not render as "you have no properties".
            let failed = false;
            const settle = (fallback) => (err) => { failed = true; console.error('Dashboard load:', err); return { data: fallback }; };
            try {
                const [o, t, p, r] = await Promise.all([
                    axios.get('/api/reports/overview').catch(settle(null)),
                    axios.get('/api/reports/trend?months=6').catch(settle({ months: [] })),
                    axios.get('/api/properties').catch(settle({ properties: [] })),
                    axios.get('/api/rent').catch(settle({ payments: [] })),
                ]);
                if (cancelled) return;
                setOverview(o.data);
                setTrend((t.data?.months || []).map(m => ({ ...m, income: Number(m.income), expenses: Number(m.expenses) })));
                setProperties(p.data?.properties || []);
                setActivity((r.data?.payments || []).slice(0, 6));
                setLoadFailed(failed);
            } finally { if (!cancelled) setLoading(false); }
        })();
        return () => { cancelled = true; };
    }, [reloadKey]);

    const greeting = React.useMemo(() => {
        const h = new Date().getHours();
        if (h < 12) return 'Good morning';
        if (h < 17) return 'Good afternoon';
        return 'Good evening';
    }, []);

    const today = new Date().toLocaleDateString('en-GB', {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
    });

    return (
        <div className="animate-fade-in space-y-6">
            <PageHeader
                title={`${greeting}, ${user?.first_name || 'there'}`}
                subtitle={today}
                actions={
                    <>
                        <Link to="/rent" className="btn-secondary">
                            <Receipt className="h-4 w-4" /> Record payment
                        </Link>
                        <Link to="/properties" className="btn-primary">
                            <Plus className="h-4 w-4" /> Add property
                        </Link>
                    </>
                }
            />

            {/* KPI row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
                <StatTile label="Income this month"
                          value={formatCurrency(overview?.income ?? 0)}
                          icon={TrendingUp} accent="success"
                          hint={overview ? `Collected of ${formatCurrency(overview.billed)}` : ''}
                          loading={loading} />
                <StatTile label="Outstanding"
                          value={formatCurrency(overview?.outstanding ?? 0)}
                          icon={AlertCircle} accent="warning"
                          hint="Unpaid across portfolio"
                          loading={loading} />
                <StatTile label="Occupancy"
                          value={overview?.occupancy?.rate != null ? `${overview.occupancy.rate}%` : '—'}
                          icon={Home} accent="primary"
                          hint={overview ? `${overview.occupancy.occupied}/${overview.occupancy.total} units` : ''}
                          loading={loading} />
                <StatTile label="Collection rate"
                          value={overview?.collection_rate != null ? `${overview.collection_rate}%` : '—'}
                          icon={Percent} accent="primary"
                          hint="Paid vs billed (MTD)"
                          loading={loading} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Income trend chart — 2/3 width */}
                <div className="lg:col-span-2">
                    <Card title="Cash flow" subtitle="Last 6 months"
                          action={<Link to="/reports" className="text-xs text-primary-700 hover:text-primary-800 inline-flex items-center gap-0.5">
                              Full reports <ArrowUpRight className="h-3 w-3" />
                          </Link>}
                          padding={false}>
                        <div className="p-5 pt-0">
                            {trend.length === 0 ? (
                                <div className="h-64 flex items-center justify-center text-sm text-neutral-400">No data yet</div>
                            ) : (
                                <ResponsiveContainer width="100%" height={260}>
                                    <AreaChart data={trend} margin={{ top: 20, right: 10, left: 0, bottom: 0 }}>
                                        <defs>
                                            <linearGradient id="gradIncome" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%"   stopColor={CHART_INCOME} stopOpacity={0.35} />
                                                <stop offset="100%" stopColor={CHART_INCOME} stopOpacity={0} />
                                            </linearGradient>
                                            <linearGradient id="gradExp" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%"   stopColor={CHART_EXPENSES} stopOpacity={0.25} />
                                                <stop offset="100%" stopColor={CHART_EXPENSES} stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid stroke={CHART_GRID} strokeDasharray="3 3" vertical={false} />
                                        <XAxis dataKey="label" stroke={CHART_AXIS} tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                                        <YAxis stroke={CHART_AXIS} tick={{ fontSize: 11 }} tickFormatter={compact} tickLine={false} axisLine={false} />
                                        <Tooltip
                                            contentStyle={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 12 }}
                                            formatter={v => formatCurrency(v)} />
                                        <Area type="monotone" dataKey="income"   stroke={CHART_INCOME}   strokeWidth={2} fill="url(#gradIncome)" name="Income" />
                                        <Area type="monotone" dataKey="expenses" stroke={CHART_EXPENSES} strokeWidth={2} fill="url(#gradExp)"    name="Expenses" />
                                    </AreaChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    </Card>
                </div>

                {/* Quick actions + occupancy — 1/3 width */}
                <div className="space-y-6">
                    <Card title="Quick actions" padding={false}>
                        <div className="divide-y divide-neutral-100">
                            <QuickAction to="/tenants" icon={Users} title="Add tenant" hint="Create user + lease" />
                            <QuickAction to="/rent" icon={Receipt} title="Record payment" hint="Cash / M-Pesa / bank" />
                            <QuickAction to="/maintenance" icon={Wrench} title="Log maintenance" hint="Assign to a vendor" />
                            <QuickAction to="/invoices" icon={DollarSign} title="Generate invoices" hint="Run this month's rent" />
                        </div>
                    </Card>

                    <Card title="Occupancy" subtitle="Portfolio snapshot">
                        {overview?.occupancy ? (
                            <OccupancyBar occ={overview.occupancy} />
                        ) : (
                            <div className="h-16 bg-neutral-50 rounded animate-pulse-soft" />
                        )}
                    </Card>
                </div>
            </div>

            {/* Bottom row: recent payments + top properties */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card title="Recent payments"
                      action={<Link to="/rent" className="text-xs text-primary-700 hover:text-primary-800 inline-flex items-center gap-0.5">
                          View all <ArrowUpRight className="h-3 w-3" />
                      </Link>}
                      padding={false}>
                    {activity.length === 0 ? (
                        <div className="p-8">
                            <EmptyState icon={Receipt} title="No payments yet" description="Payments recorded manually or via Family Bank / M-Pesa will show up here." />
                        </div>
                    ) : (
                        <ul className="divide-y divide-neutral-100">
                            {activity.map(p => (
                                <li key={p.id} className="flex items-center gap-3 px-5 py-3 hover:bg-neutral-50">
                                    <div className="w-9 h-9 rounded-full bg-success-50 text-success-700 flex items-center justify-center flex-shrink-0">
                                        <CheckCircle2 className="h-4 w-4" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="text-sm font-medium text-neutral-900 truncate">{p.tenant_name}</div>
                                        <div className="text-xs text-neutral-500 flex items-center gap-1.5">
                                            <span className="truncate">{p.property_name}</span>
                                            <span>·</span>
                                            <span className="capitalize">{(p.payment_method || '').replace('_',' ')}</span>
                                        </div>
                                    </div>
                                    <div className="text-right flex-shrink-0">
                                        <div className="text-sm font-semibold text-neutral-900 tabular-nums">{formatCurrency(p.amount)}</div>
                                        <div className="text-xs text-neutral-500 flex items-center gap-1 justify-end">
                                            <Clock className="h-3 w-3" />
                                            {new Date(p.payment_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </Card>

                <Card title="Properties needing attention"
                      subtitle="Vacant or with outstanding balances"
                      action={<Link to="/properties" className="text-xs text-primary-700 hover:text-primary-800 inline-flex items-center gap-0.5">
                          All properties <ArrowUpRight className="h-3 w-3" />
                      </Link>}
                      padding={false}>
                    {loadFailed && properties.length === 0 ? (
                        <div className="p-8">
                            <EmptyState icon={Building2} title="Couldn't load your portfolio"
                                description="The dashboard could not reach the server. Your data has not changed."
                                action={<button type="button" className="btn-primary" onClick={() => setReloadKey(k => k + 1)}>
                                    Try again
                                </button>} />
                        </div>
                    ) : properties.length === 0 ? (
                        <div className="p-8">
                            <EmptyState icon={Building2} title="No properties yet"
                                description="Add your first property to start managing rent and tenants."
                                action={<Link to="/properties" className="btn-primary">
                                    <Plus className="h-4 w-4" /> Add property
                                </Link>} />
                        </div>
                    ) : (
                        <ul className="divide-y divide-neutral-100">
                            {[...properties].sort((a,b) => Number(b.outstanding_balance||0) - Number(a.outstanding_balance||0)).slice(0,5).map(p => (
                                <li key={p.id} className="px-5 py-3 hover:bg-neutral-50 flex items-center gap-3">
                                    <div className="w-9 h-9 rounded-lg bg-neutral-100 text-neutral-500 flex items-center justify-center flex-shrink-0">
                                        <Building2 className="h-4 w-4" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="text-sm font-medium text-neutral-900 truncate">
                                            {p.house_number && <span className="text-primary-700 mr-1">#{p.house_number}</span>}
                                            {p.name}
                                        </div>
                                        <div className="text-xs text-neutral-500 truncate">{p.city}</div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {Number(p.outstanding_balance) > 0 && (
                                            <Badge tone="warning">{formatCurrency(p.outstanding_balance)} owed</Badge>
                                        )}
                                        <Badge tone={p.status === 'occupied' ? 'success' : p.status === 'available' ? 'neutral' : 'warning'} dot>
                                            {p.status}
                                        </Badge>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </Card>
            </div>
        </div>
    );
};

const QuickAction = ({ to, icon: Icon, title, hint }) => (
    <Link to={to} className="flex items-center gap-3 px-5 py-3 hover:bg-neutral-50 transition-colors">
        <div className="w-9 h-9 rounded-lg bg-primary-50 text-primary-700 flex items-center justify-center flex-shrink-0">
            <Icon className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-neutral-900">{title}</div>
            <div className="text-xs text-neutral-500">{hint}</div>
        </div>
        <ChevronRight className="h-4 w-4 text-neutral-400" />
    </Link>
);

const OccupancyBar = ({ occ }) => {
    const total = Math.max(1, occ.total);
    const parts = [
        { key: 'Occupied',    value: occ.occupied,    color: 'bg-primary-600' },
        { key: 'Available',   value: occ.available,   color: 'bg-neutral-300' },
        { key: 'Maintenance', value: occ.maintenance, color: 'bg-warning-500' },
    ];
    return (
        <div>
            <div className="flex items-baseline gap-2">
                <div className="text-3xl font-semibold text-neutral-900 tabular-nums">
                    {occ.rate != null ? `${occ.rate}%` : '—'}
                </div>
                <div className="text-xs text-neutral-500">{occ.occupied}/{occ.total} occupied</div>
            </div>
            <div className="mt-3 flex h-2 rounded-full overflow-hidden bg-neutral-100">
                {parts.map(p => (
                    <div key={p.key} className={p.color} style={{ width: `${(p.value / total) * 100}%` }} />
                ))}
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                {parts.map(p => (
                    <div key={p.key} className="flex items-center gap-1.5">
                        <span className={`w-2 h-2 rounded-full ${p.color}`} />
                        <span className="text-neutral-500">{p.key}</span>
                        <span className="text-neutral-900 font-medium tabular-nums ml-auto">{p.value || 0}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default Dashboard;
